// Copyright (c) Hamilton Manufacturing Corp.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { QuestRoute } from '../_common/api/QuestRoutes';
import { between, htmlDecode } from '../_common/api/utility';
import Counter from '../_common/Counter';
import Icon from '../_common/form/Icon';
import Title from '../_common/form/Title';
import { useGlobalState } from '../_common/globalState/GlobalStateProvider';
import Modal from '../_common/Modal';
import { HamQuest } from '../../App';
import LoyaltyPoints from './pages/LoyaltyPoints';
import { HasFeature, Features } from '../_common/RequireFeature';

const Wrapper = styled.div`
    width: 100%;
    box-sizing: border-box;
    flex: ${({ compact }) => compact ? '1' : 'unset'};
    display: flex;
    flex-direction: column;
    align-items: ${({ compact, solo }) => compact && !solo ? 'center' : compact ? 'stretch' : 'center'};
    justify-content: ${({ compact, solo }) => compact ? (solo ? 'center' : 'flex-start') : 'space-between'};
    min-height: ${({ compact, solo }) => compact ? (solo ? '8.6rem' : '11.2rem') : 'unset'};
    padding: ${({ compact, solo }) => compact ? (solo ? '0.9rem 1rem' : '1rem 0.9rem') : '0.6rem'};
    margin: ${({ compact }) => compact ? '0' : '0 0 0.5em 0'};
    border-radius: 1rem;
    color: ${({ compact, theme }) => compact ? (theme.mobile.loyaltyCard.compactText || '#ffffff') : theme.mobile.loyaltyCard.text};
    background: ${({ compact, theme }) => compact ? (theme.mobile.loyaltyCard.compactBackground || '#66c887') : theme.mobile.loyaltyCard.background};
    text-align: ${({ compact, solo }) => compact && !solo ? 'center' : compact ? 'left' : 'center'};
    box-shadow: 1px 1px 5px 1px ${({ theme }) => theme.mobile.shadow};
    text-decoration: none;
`
const Header = styled.div`
    font-weight: bold;
    font-size: 1.2rem;
`

const HeaderRow = styled.div`
    display: flex;
    flex-direction: ${({ compact, solo }) => compact && !solo ? 'column' : 'row'};
    align-items: center;
    justify-content: center;
    gap: ${({ compact, solo }) => compact && !solo ? '0.35rem' : '0.45rem'};
`

const ProgressWrap = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: auto;
`

const CompactRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
`

const CompactText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    flex: 1;
`

const CompactRatio = styled.div`
    font-size: 1.3rem;
    font-weight: 700;
    line-height: 1.1;
    margin-top: 0.15rem;
`

const AwardTitle = styled.div`
    font-size: 1.05rem;
    font-weight: 800;
    text-transform: uppercase;
    line-height: 1.2;
`

const RewardLabel = styled.div`
    font-size: 0.85rem;
    font-weight: 500;
    opacity: 0.9;
    line-height: 1.2;
`

const GuestMessage = styled.div`
    margin-top: auto;
    font-size: ${({ solo }) => solo ? '1rem' : '0.95rem'};
    font-weight: 700;
    line-height: 1.15;
    max-width: ${({ solo }) => solo ? 'none' : '10rem'};
    text-align: ${({ solo }) => solo ? 'left' : 'center'};
`

const Heart = styled(Icon)`
    font-size: 3em;
    padding: 0.2em;
    color: ${({ theme }) => theme.error}
`

const SoloIcon = styled(Icon)`
    font-size: 1.5rem;
`
const Col = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 0.2em;
`
const Row = styled(Link)`
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
`

const CircleShell = styled.div`
    width: ${({ size }) => size};
    height: ${({ size }) => size};
    border-radius: 50%;
    padding: ${({ ringWidth }) => ringWidth};
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme, progress }) => `conic-gradient(${theme.mobile.loyaltyCard.compactAccent} ${progress}%, ${theme.mobile.loyaltyCard.parentBar} ${progress}% 100%)`};
    box-shadow: inset 0 0 0 1px #ffffff26;
    transition: background 0.35s;
`

const CircleCenter = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: ${({ compact, theme }) => compact ? (theme.mobile.loyaltyCard.compactCenter || '#70d194') : theme.mobile.loyaltyCard.background};
    color: ${({ compact, theme }) => compact ? (theme.mobile.loyaltyCard.compactCenterText || '#ffffff') : theme.mobile.loyaltyCard.text};
    text-align: center;
`

const PointsValue = styled.div`
    font-size: ${({ compact }) => compact ? '1.15rem' : '1.4rem'};
    font-weight: 800;
    line-height: 1;
`

const PointsLabel = styled.div`
    margin-top: 0.15rem;
    font-size: ${({ compact }) => compact ? '0.7rem' : '0.8rem'};
    opacity: 0.95;
`


const PointsBar = ({ points, goal, site_config, compact = false, loading = false }) => {
    const safePoints = points ?? 0;
    const safeGoal = goal ?? 0;
    const progress = loading ? 0 : between(safeGoal > 0 ? ((safePoints / safeGoal) * 100) : 0, 0, 100);

    return (
        <CircleShell
            progress={progress}
            size={compact ? '4.8rem' : '6.5rem'}
            ringWidth={compact ? '0.5rem' : '0.62rem'}
        >
            <CircleCenter compact={compact ? 1 : 0}>
                {loading ? null : <>
                    <PointsValue compact={compact ? 1 : 0}>{safePoints ? <Counter to={safePoints} /> : 0}</PointsValue>
                    <PointsLabel compact={compact ? 1 : 0}>pts</PointsLabel>
                </>}
            </CircleCenter>
        </CircleShell>
    );
};

const LoyaltyCard = ({ ...props }) => {
    const {
        state: {
            active_user,
            site_config
        },
        getLoyaltyAwards
    } = useGlobalState();

    const [points, setPoints] = useState(null);
    const [goal, setGoal] = useState(null)
    const [title, setTitle] = useState(' ')
    const [details, setDetails] = useState(null);
    const widgetText = site_config.homeWidgetText || {};

    const loyaltyPointFlexEnabled = HasFeature(Features.AwardEnhancement);

    // Use awards from global state
    const awards = site_config.loyaltyAwards || [];

    const callPoints = React.useCallback(() => {
        getLoyaltyAwards();
        HamQuest.get(QuestRoute.user.loyaltyPoints)
            .then(res => {
                if (window.location.href.match('#MAC')) {
                    setPoints(res.point_goal / 2)
                } else {
                    setPoints(res.current_points)
                }
                setGoal(res.point_goal)
                setTitle(res.widget_title)
                setDetails(res.widget_desc)
            });
    }, [getLoyaltyAwards]);

    useEffect(() => {
        if (!active_user.isLoggedIn) return;
        callPoints();
    }, [active_user.isLoggedIn, callPoints]);

    const showInfo = () => {
        Modal.Show({
            title: 'Loyalty Points',
            body: loyaltyPointFlexEnabled ? <LoyaltyPoints /> : 
            <div>
                {details ? <div dangerouslySetInnerHTML={{ __html: htmlDecode(details) || details }} /> : null}
                <br />
                Your Points: {points ? <Counter to={points} /> : 0} / {goal?.toLocaleString() || 0}
            </div>,
            icon: 'sideCar',
        })
    }

    const maxPoints = awards != null && awards.length > 0 ? awards[awards.length - 1].pointCost : 0;
    const progressGoal = loyaltyPointFlexEnabled ? maxPoints : goal;
    const compactSolo = props.compact && props.solo;
    const loaded = points !== null && goal !== null;

    if (active_user.isLoggedIn) return (
        <Wrapper onClick={showInfo} compact={props.compact ? 1 : 0} solo={props.solo ? 1 : 0}>
            {compactSolo ?
                <CompactRow>
                    <CompactText>
                        <SoloIcon icon='award' style={{ marginBottom: '0.3rem' }} />
                        {loaded && title?.trim() ? <AwardTitle>FREE {title}</AwardTitle> : null}
                        <RewardLabel>{widgetText.loyaltySignedInTitle || 'Reward Progress'}</RewardLabel>
                        <CompactRatio>{loaded ? <>{points ? <Counter to={points} /> : 0} / {goal?.toLocaleString() || 0}</> : null}</CompactRatio>
                    </CompactText>
                    <div>
                        <PointsBar points={points} goal={progressGoal} compact loading={!loaded} />
                    </div>
                </CompactRow>
                :
                <>
                    <HeaderRow compact={props.compact ? 1 : 0} solo={props.solo ? 1 : 0}>
                        <Icon icon='award' />
                        <Header>{widgetText.loyaltySignedInTitle || 'Reward Progress'}</Header>
                    </HeaderRow>

                    <ProgressWrap>
                        <PointsBar points={points} goal={progressGoal} compact={props.compact} loading={!loaded} />
                    </ProgressWrap>
                </>
            }

            {!props.compact && loaded ?
                <div style={{ width: '85%', fontSize: '0.9em' }}>
                    Reach {goal?.toLocaleString() || 0} points to receive a <b>FREE</b> {title}
                </div>
                : null}

            {!props.compact && loaded ? <b>{points ? <Counter to={points} /> : 0} / {goal?.toLocaleString() || 0}</b> : null}
        </Wrapper>
    );

    if (props.compact) {
        return (
            <Wrapper compact={1} solo={props.solo ? 1 : 0} as={Link} to='/logIn'>
                {props.solo ?
                    <CompactRow>
                        <CompactText>
                            <HeaderRow compact={1} solo={1}>
                                <SoloIcon icon='award' />
                                <Header>{widgetText.loyaltyGuestTitle || 'Loyalty Rewards'}</Header>
                            </HeaderRow>
                            <GuestMessage solo={1}>{widgetText.loyaltyGuestSoloMessage || 'SIGN UP TO USE OUR LOYALTY PROGRAM & EARN FREE WASHES!'}</GuestMessage>
                        </CompactText>
                    </CompactRow>
                    :
                    <>
                        <HeaderRow compact={1} solo={0}>
                            <Icon icon='award' />
                            <Header>{widgetText.loyaltyGuestTitle || 'Loyalty Rewards'}</Header>
                        </HeaderRow>
                        <GuestMessage>{widgetText.loyaltyGuestMessage || 'SIGN UP TO EARN FREE WASHES!'}</GuestMessage>
                    </>}
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <Row to='/logIn'>
                <Heart icon='heart' />
                <Col>
                    <Title>SIGN UP TO USE OUR LOYALTY PROGRAM!</Title>
                    <div>Get points with every purchase and earn a free wash!</div>
                </Col>
            </Row>
        </Wrapper>
    );
}

export default LoyaltyCard;
export { PointsBar };